export const AUDIT_LOG_ACTION_LABELS = {
  login: "Login",
  logout: "Logout",
  two_factor_verify: "2FA Verified",
  two_factor_enable: "2FA Enabled",
  two_factor_disable: "2FA Disabled",
  page_view: "Page View",
  customer_create: "Customer Created",
  customer_update: "Customer Updated",
  customer_suspend: "Customer Suspended",
  customer_unsuspend: "Customer Activated",
  customer_terminate: "Customer Terminated",
  customer_delete: "Customer Deleted",
  customer_export: "Customer Export",
  customer_database_provision: "Customer Database Provisioned",
  plan_update: "Plan Updated",
  subscription_update: "Subscription Updated",
  mc_user_create: "User Created",
  mc_user_update: "User Updated",
  mc_user_delete: "User Deleted",
  tag_create: "Tag Created",
  tag_delete: "Tag Deleted"
};

export const AUDIT_LOG_CATEGORY_LABELS = {
  auth: "Auth",
  customer: "Customer",
  plan: "Plan",
  subscription: "Subscription",
  user: "User",
  export: "Export",
  tag: "Tag"
};

const METADATA_FIELD_LABELS = {
  customer_domain: "Domain",
  primary_admin_email: "Admin email",
  previous_status: "Previous status",
  new_status: "New status",
  export_type: "Export type",
  count: "Records",
  plan_type: "Plan type",
  disk_space: "Disk space",
  max_users: "Max users",
  operation: "Operation",
  job_title: "Job title"
};

export const getAuditLogActionLabel = action =>
  AUDIT_LOG_ACTION_LABELS[action] || action;

export const getAuditLogCategoryLabel = category =>
  AUDIT_LOG_CATEGORY_LABELS[category] || category;

export const getAuditLogCategoryBadgeClass = category => {
  const map = {
    auth: "audit-logs__badge--auth",
    customer: "audit-logs__badge--customer",
    plan: "audit-logs__badge--plan",
    subscription: "audit-logs__badge--subscription",
    user: "audit-logs__badge--user",
    export: "audit-logs__badge--export",
    tag: "audit-logs__badge--tag"
  };
  return map[category] || "audit-logs__badge--default";
};

const formatMetadataValue = value => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const getLegacyAuditLogSummary = (action, metadata = {}) => {
  if (metadata?.summary) {
    return metadata.summary;
  }

  switch (action) {
    case "login":
      if (metadata.is_two_factor_enabled) {
        return "Password verified — 2FA verification required";
      }
      return "Signed in successfully";
    case "logout":
      return "Signed out";
    case "two_factor_verify":
      return "2FA code verified — session unlocked";
    case "customer_suspend":
      return metadata.customer_domain
        ? `Suspended customer (${metadata.customer_domain})`
        : "Customer suspended";
    case "customer_unsuspend":
      return metadata.customer_domain
        ? `Activated customer (${metadata.customer_domain})`
        : "Customer activated";
    case "customer_delete":
      return metadata.customer_domain
        ? `Started deletion for ${metadata.customer_domain}`
        : "Customer deletion started";
    case "customer_export":
      return metadata.count
        ? `Exported ${metadata.count} customer(s)`
        : "Customer export";
    default:
      return null;
  }
};

export const formatAuditLogDetails = log => {
  const metadata = log?.metadata;
  const summary = getLegacyAuditLogSummary(log?.action, metadata);
  if (summary) {
    return summary;
  }

  if (!metadata || typeof metadata !== "object") {
    return "-";
  }

  const entries = Object.entries(metadata).filter(
    ([key]) => key !== "summary" && key !== "changes"
  );
  if (!entries.length) {
    return "-";
  }

  return entries
    .map(([key, value]) => {
      const formattedValue = formatMetadataValue(value);
      if (!formattedValue) {
        return null;
      }
      const label =
        METADATA_FIELD_LABELS[key] ||
        key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
      return `${label}: ${formattedValue}`;
    })
    .filter(Boolean)
    .join(" · ");
};

export const getAuditLogChanges = log => {
  const changes = log?.metadata?.changes;
  return Array.isArray(changes) ? changes : [];
};

export const hasExpandableAuditLogDetails = log =>
  getAuditLogChanges(log).length > 0;

export const getAuditLogDetailsPreview = log => ({
  summary: formatAuditLogDetails(log)
});

export const formatAuditLogTarget = log => {
  const explicitTarget = log?.target_label || log?.targetLabel;
  if (explicitTarget) {
    return explicitTarget;
  }

  const domain = log?.metadata?.customer_domain;
  if (domain) {
    return domain;
  }

  return "—";
};
